import { FiTaskV2HelperService } from '@fi-app/task-v2/fi-task-v2-helper.service';
import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { FiTasksViewMode } from '@fi-shared/fi-task-shared/fi-task-enums';
import { FiTaskStatusType } from '@fi-app/task-v2/fi-task-v2-enums';

interface IStatusOption {
    value: FiTaskStatusType;
    textId: string;
    iconClass: string;
    isHidden?: () => boolean;
    isDisabled?: () => boolean;
}

@Component({
    selector: 'fi-task-v2-status-select',
    templateUrl: './fi-task-v2-status-select.component.html',
    styleUrls: ['./fi-task-v2-status-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-task-v2-status-select' },
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiTaskV2StatusSelectComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiTaskV2StatusSelectComponent,
            multi: true,
        },
    ],
})
export class FiTaskV2StatusSelectComponent
    extends ControlValueAccessorValidatorBase<FiTaskStatusType>
    implements OnInit, OnChanges
{
    @Input({ required: true }) createdByUserId: number;
    @Input({ required: true }) viewMode: FiTasksViewMode;
    @Input() isDisabled: boolean = false;
    @Input() isAutomatedTask: boolean = false;

    protected currentStatusOption: IStatusOption | undefined;
    protected readonly statusOptions: Readonly<IStatusOption[]> = [
        {
            value: FiTaskStatusType.NotStarted,
            textId: 'checklist.not_started',
            iconClass: 'far fa-clipboard regular',
        },
        {
            value: FiTaskStatusType.InProcess,
            textId: 'taskv2.in_progress',
            iconClass: 'far fa-hourglass-half blue',
        },
        {
            value: FiTaskStatusType.Completed,
            textId: 'common.completed',
            iconClass: 'far fa-clipboard-check green',
        },
        {
            value: FiTaskStatusType.Cancelled,
            textId: 'common.cancelled',
            iconClass: 'far fa-circle-x red',
            isHidden: () => {
                return (
                    this.fiTaskV2HelperService.isCancelledOptionHidden(
                        this.viewMode,
                        this.createdByUserId,
                        this.currentStatusOption?.value,
                    ) || this.isAutomatedTask
                );
            },
            isDisabled: () => this.fiTaskV2HelperService.isCancelledOptionDisabled(this.viewMode, this.createdByUserId),
        },
    ];

    constructor(
        injector: Injector,
        private fiTaskV2HelperService: FiTaskV2HelperService,
    ) {
        super(injector);
    }

    public ngOnInit(): void {}

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes.ngModel;
        if (change) {
            this.setCurrentStatusOption();
        }
    }

    protected onStatusOptionChanged(newStatus: FiTaskStatusType): void {
        this.ngModel = newStatus;
        this.setCurrentStatusOption();
        this.doPropagateChanges();
    }

    private setCurrentStatusOption(): void {
        this.currentStatusOption = this.statusOptions.find((option) => option.value === this.ngModel);
    }
}
